const SANDBOX_PREFIX = "sandbox:/mnt/data/"

const MIME_BY_EXT: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  svg: "image/svg+xml",
}

export function mimeFromFilename(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() ?? ""
  return MIME_BY_EXT[ext] ?? "image/png"
}

function basename(ref: string): string {
  const withoutQuery = ref.split(/[?#]/)[0]
  const parts = withoutQuery.split("/")
  return parts[parts.length - 1] || withoutQuery
}

function decodePercent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value // malformed escape: keep the raw name
  }
}

/** The name as written, with "&amp;" unescaped (html attributes) and/or %-escapes decoded. */
function nameSpellings(base: string): string[] {
  const unescaped = base.replace(/&amp;/g, "&")
  const spellings = [base, unescaped, decodePercent(base), decodePercent(unescaped)]
  // A decoded "/" (%2F) must not turn the name into a path that matches another file.
  return [...new Set(spellings)].filter((name) => name === base || !name.includes("/"))
}

/**
 * Maps an image reference (as it appears in artifact content) to a known
 * session filename, or null. The last path segment may be %-encoded or carry
 * "&amp;" (#3595). Never matches data: URIs or external http(s) or
 * protocol-relative (//host/…) URLs.
 */
export function resolveRefToFilename(ref: string, filenames: string[]): string | null {
  if (!ref) return null
  if (/^data:/i.test(ref) || /^https?:/i.test(ref) || ref.startsWith("//")) return null
  const stripped = ref.startsWith(SANDBOX_PREFIX) ? ref.slice(SANDBOX_PREFIX.length) : ref
  const direct = filenames.find((f) => stripped === f || stripped.endsWith("/" + f))
  if (direct) return direct
  return nameSpellings(basename(stripped)).find((name) => filenames.includes(name)) ?? null
}

/** Converts image bytes to a base64 data: URI, chunking to avoid call-stack limits. */
export function bytesToDataUri(bytes: ArrayBuffer, mime: string): string {
  const view = new Uint8Array(bytes)
  let binary = ""
  const chunk = 0x8000
  for (let i = 0; i < view.length; i += chunk) {
    binary += String.fromCharCode(...view.subarray(i, i + chunk))
  }
  return `data:${mime};base64,${btoa(binary)}`
}
